import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Activity, Search, ArrowUpRight, ArrowDownRight, RefreshCcw, Download, Printer, CheckCircle2, XCircle } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { fetchTransactionStats, fetchMerchantTransactions } from '../services/api';
import toast from 'react-hot-toast';
import Select from '../components/ui/Select';
import { PERMISSIONS } from '../utils/permissions';
import HasPermission from '../components/HasPermission';

const Naira = ({ size = 20, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M6 3v18" />
    <path d="M18 3v18" />
    <path d="M6 3l12 18" />
    <path d="M3 10h18" />
    <path d="M3 14h18" />
  </svg>
);


const StatCard = ({ title, value, subtext, icon: Icon, trend }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-center justify-between space-y-0 pb-2">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-slate-500'}`}>
          {trend > 0 ? `+${trend}% from last month` : subtext}
        </p>
      </div>
    </CardContent>
  </Card>
);

const TransactionIcon = ({ type, status }) => {
  if (status === 'Failed') return <div className="p-2 rounded-full bg-red-100 text-red-600"><Activity size={16} /></div>;
  if (type === 'Payout') return <div className="p-2 rounded-full bg-orange-100 text-orange-600"><ArrowUpRight size={16} /></div>;
  if (type === 'Collection') return <div className="p-2 rounded-full bg-green-100 text-green-600"><ArrowDownRight size={16} /></div>;
  return <div className="p-2 rounded-full bg-slate-100 text-slate-600"><RefreshCcw size={16} /></div>;
};

const DashboardPage = () => {
  const { user } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParam, setSearchParam] = useState('ref');
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.merchantId) return;

      try {
        const [statsRes, transRes] = await Promise.all([
          fetchTransactionStats({ merchantId: user.merchantId }),
          fetchMerchantTransactions({ size: 10 })
        ]);

        if (statsRes.data.status) {
          setStats(statsRes.data.data);
        }

        if (transRes.data.status) {
          setTransactions(transRes.data.data.content || transRes.data.data || []);
        }
      } catch (err) {
        console.log(err)
        toast.error('Failed to load dashboard data');
      } finally {
        return
      }
    };

    fetchData();
  }, [user?.merchantId]);

  // Filter fetched transactions
  const filteredTransactions = transactions.filter(tx => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;

    // Mapping real fields: ref -> reference, amount -> amount
    if (searchParam === 'amount') return String(tx.amount).toLowerCase().includes(term);
    if (searchParam === 'accountNum') return String(tx.accountNumber || '').includes(term);
    if (searchParam === 'ref') return String(tx.reference || '').toLowerCase().includes(term);

    return true;
  });

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    try {
      // Create CSV headers
      const headers = ['Reference', 'Type', 'Amount', 'Status', 'Account Number', 'Date'];

      // Convert filtered transactions to CSV rows
      const csvRows = filteredTransactions.map(tx => {
        // Remove commas from amount for CSV safety
        const safeAmount = tx.amount.replace(/,/g, '');
        return [
          tx.ref,
          tx.type,
          safeAmount,
          tx.status,
          `="${tx.accountNum}"`, // Force Excel to treat as string
          `"${tx.timestamp}"`    // Wrap date in quotes
        ].join(',');
      });

      // Combine headers and rows
      const csvContent = [headers.join(','), ...csvRows].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `b2b_transactions_export_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Export downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export transactions.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Overview</h2>
          <p className="text-sm text-slate-500 mt-1">
            Welcome back, {user?.firstName || 'Merchant'}. Here's what's happening today.
          </p>
        </div>
        <HasPermission permission={PERMISSIONS.EXPORT_TRANSACTIONS}>
          <div className="flex items-center gap-3 print:hidden">
            <Button variant="primary" className="gap-2" onClick={handleExport}>
              <Download size={16} /> Export
            </Button>
            <Button variant="secondary" className="gap-2" onClick={handlePrint}>
              <Printer size={16} /> Print
            </Button>
          </div>
        </HasPermission>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Revenue"
          value={stats ? `₦${Number(stats.totalVolume || 0).toLocaleString()}` : '₦0.00'}
          trend={stats?.revenueTrend}
          icon={Naira}
        />
        <StatCard
          title="Transactions"
          value={stats ? stats.totalCount || 0 : '0'}
          trend={stats?.transactionTrend}
          icon={Activity}
        />
        <StatCard
          title="Successful"
          value={stats ? stats.successfulCount || 0 : '0'}
          subtext={`${stats?.successRate || 0}% Success Rate`}
          icon={CheckCircle2}
        />
        <StatCard
          title="Failed"
          value={stats ? stats.failedCount || 0 : '0'}
          subtext="Requires attention"
          icon={XCircle}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">

        {/* Recent Transactions Card */}
        <Card className="col-span-4 flex flex-col h-[500px]">
          <CardHeader className="pb-4 border-b border-slate-100 shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <CardTitle>Recent Transactions</CardTitle>
              {/* Search Filter Input with explicit Selector */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Select
                  value={{ value: searchParam, label: searchParam === 'ref' ? 'Reference' : searchParam === 'amount' ? 'Amount' : 'Account' }}
                  onChange={(option) => setSearchParam(option.value)}
                  options={[
                    { value: 'ref', label: 'Reference' },
                    { value: 'amount', label: 'Amount' },
                    { value: 'accountNum', label: 'Account' }
                  ]}
                  className="w-full sm:w-40"
                />
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`Search ${searchParam}...`}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg transition-colors"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm h-full flex flex-col items-center justify-center">
                <Search className="mx-auto h-10 w-10 text-slate-200 mb-3" />
                <p>No transactions match your search.</p>
              </div>
            ) : (
              <div className="p-6 relative">
                {/* Vertical line connecting the timeline items */}
                <div className="absolute top-6 bottom-6 left-10 w-[2px] bg-slate-100 -z-10 hidden sm:block"></div>

                <div className="space-y-6">
                  {filteredTransactions.map((tx) => (
                    <div key={tx.id || tx.reference} className="flex gap-4 group">
                      {/* Interactive Timeline Icon */}
                      <div className="relative shrink-0 flex items-start z-10 sm:bg-white sm:py-1">
                        <TransactionIcon type={tx.type || 'Transaction'} status={tx.status} />
                      </div>

                      {/* Transaction Detail Card */}
                      <div className="flex-1 min-w-0 bg-white border border-slate-100 p-4 rounded-xl group-hover:border-slate-200 transition-colors shadow-sm">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-slate-900 truncate">
                              {tx.type || 'Transaction'} • ₦{Number(tx.amount || 0).toLocaleString()}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-500 font-medium">{tx.reference}</span>
                              <span className="text-xs text-slate-400">• Acc: {tx.accountNumber || 'N/A'}</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium tracking-wide border
                                ${tx.status === 'Success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}
                              `}>
                              {tx.status?.toUpperCase()}
                            </span>
                            <p className="text-[11px] text-slate-400 mt-1.5">{tx.transactionDate || tx.timestamp}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* API Usage Card */}
        <Card className="col-span-3 h-fit">
          <CardHeader>
            <CardTitle>API Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">Requests</span>
                  <span className="text-slate-500">0 / 0</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-primary rounded-full w-[0%]"></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
